import cn from "classnames"
import s from "./InitialStep.module.scss"
import { Button } from "src/components/button/Button"
import { Heading, Only } from "src/utils"
import { InvestmentsGraph } from "src/components/investments-graph/InvestmentsGraph"
import { useBus } from "react-bus"
import { useEffect, useState } from "react"
import { calculateIncomeFromRate } from "src/lib/investmentHandler"
import { numberWithDecimal, numberWithThousandSeparator } from "src/lib/numbers"

import { CSSProperties } from "react"
import { useRouter } from "next/router"

interface LegendCSSProperties extends CSSProperties {
    "--dot-color": string
}

interface InitialStepProps {
    onNextStep: () => void
    sharedClasses: {
        stepContainer: string
        textContainer: string
        description: string
    }
    initialStepHeading: string
    initialStepDescription: string
    initialStepButtonLabel: string
    initialStepExamplePrincipal: number
    initialStepExampleMontlyAmount: number
    initialStepInfoboxHeadline: string
    initialStepInfoboxSubHeadline: string
    initialStepInfoboxPrincipalLabel: string
    initialStepInfoboxRateLabel: string
    initialStepInfoboxMonthlyPaymentsLabel: string
    initialStepInfoboxMaxYearsUnitLabel: string
    initialStepInfoboxMaxYearsLabel: string
    rate: number
    period: number
    maxYears: number
    currency: string
    riskInfo: string
    zeroInterestRateLabel: string
    estimatedRefundLabel: string
    tooltipTitle: string
}

export default function InitialStep({
    sharedClasses,
    initialStepHeading,
    initialStepDescription,
    initialStepButtonLabel,
    initialStepExamplePrincipal,
    initialStepExampleMontlyAmount,
    initialStepInfoboxHeadline,
    initialStepInfoboxSubHeadline,
    initialStepInfoboxPrincipalLabel,
    initialStepInfoboxRateLabel,
    initialStepInfoboxMonthlyPaymentsLabel,
    initialStepInfoboxMaxYearsUnitLabel,
    initialStepInfoboxMaxYearsLabel,
    rate,
    period,
    maxYears,
    currency,
    riskInfo,
    zeroInterestRateLabel,
    estimatedRefundLabel,
    tooltipTitle,
}: InitialStepProps) {
    const [render, setRender] = useState(false)

    const { locale } = useRouter()
    const bus = useBus()

    const handleClick = () => {
        bus.emit("investmentOverlay")
    }

    const principal = initialStepExamplePrincipal || 100000
    const montlyAmount = initialStepExampleMontlyAmount || 500

    const earnings = calculateIncomeFromRate(rate, principal, montlyAmount, period, maxYears, true)
    const flatEarnings = calculateIncomeFromRate(0.0, principal, montlyAmount, period, maxYears, true)

    const data = []

    earnings.forEach((e, index) => {
        const currentEarning = {
            year: e.x,
            earning: e.y,
            flatEarning: flatEarnings[index].y,
        }
        data.push(currentEarning)
    })

    useEffect( () => {
        setRender(true)

        return () => {
            setRender(false)
        }
    }, [])

    return (
        <div className={sharedClasses.stepContainer}>
            <div className={cn(s["initial-step__text"], sharedClasses.textContainer)}>
                <Heading className="heading-300">{initialStepHeading}</Heading>
                <p className={cn(sharedClasses.description, "paragraph-100")}>
                    {initialStepDescription}
                </p>
            </div>
            <div className={s["initial-step__graph"]}>
                <div className={s["initial-step__graph-wrapper"]}>

                    <Only if={ render }>
                        <div className={ s["graph"]}>
                            <InvestmentsGraph tooltipTitle={tooltipTitle} zeroInterestRateLabel={zeroInterestRateLabel} estimatedRefundLabel={estimatedRefundLabel} currency={ currency } data={data}/>
                        </div>
                    </Only>
                    <div className={ s["infobox"] }>
                        <Heading className={ cn(s["infobox__headline"] , "heading-50") }>{ initialStepInfoboxHeadline }</Heading>

                        <Heading className={ cn(s["infobox__sub-headline"] , "heading-50") }>{ numberWithThousandSeparator(earnings?.[earnings.length - 1].y)} {currency} {initialStepInfoboxSubHeadline}</Heading>

                        <ul className={ s["infobox__list"] }>
                            <li><span className="label-300">{[numberWithThousandSeparator(principal), currency].join(" ")}</span> { initialStepInfoboxPrincipalLabel }</li>
                            <li><span className="label-300">{numberWithDecimal(rate, locale)}%</span> { initialStepInfoboxRateLabel }</li>
                            <li><span className="label-300">{[numberWithThousandSeparator(montlyAmount), currency].join(" ")}</span> { initialStepInfoboxMonthlyPaymentsLabel }</li>
                            <li><span className="label-300">{ [maxYears, initialStepInfoboxMaxYearsUnitLabel].join(" ")}</span> { initialStepInfoboxMaxYearsLabel }</li>
                        </ul>

                        <ul className={ s["infobox__legends"] }>
                            <li className={ s["infobox__legend"] } style={{
                                "--dot-color": "#73CCA3",
                            } as LegendCSSProperties}>{ estimatedRefundLabel }</li>
                            <li className={ s["infobox__legend"] } style={{
                                "--dot-color": "#004D43",
                            } as LegendCSSProperties}>{ zeroInterestRateLabel }</li>

                        </ul>
                    </div>
                </div>
                {riskInfo && <p className={cn(s["initial-step__label"], "paragraph-50")}>{riskInfo}</p>}
            </div>
            <Button pill center onClick={handleClick} className={s["initial-step__btn"]}>
                {initialStepButtonLabel}
            </Button>
        </div>
    )
}
